import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, X, ChevronRight } from 'lucide-react';
import api from '../../services/api';

export default function Subjects() {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({ subject_name: '', subject_code: '', description: '' });
  const [error, setError] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [subjectsRes, gradesRes] = await Promise.all([
        api.get('/admin/subjects'),
        api.get('/admin/grades')
      ]);
      if (subjectsRes.data.success) setSubjects(subjectsRes.data.subjects || []);
      if (gradesRes.data.success) setGrades(gradesRes.data.grades || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // Basic client-side validation: prevent duplicate subject_code
      const code = (formData.subject_code || '').trim();
      const duplicate = subjects.find(s => s.subject_code && s.subject_code.toLowerCase() === code.toLowerCase() && (!editingSubject || s.subject_id !== editingSubject.subject_id));
      if (duplicate) {
        setError('A subject with this code already exists');
        return;
      }

      // No grade_id needed - subjects are now assigned via grades page
      const payload = { ...formData, grade_id: 0 };

      if (editingSubject) {
        await api.put(`/admin/subjects/${editingSubject.subject_id}`, payload);
      } else {
        await api.post('/admin/subjects', payload);
      }
      setShowModal(false);
      setEditingSubject(null);
      setFormData({ subject_name: '', subject_code: '', description: '' });
      loadData();
    } catch (err) {
      // Prefer server error message under `error`, fall back to `message` or generic text
      const serverMsg = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(serverMsg || 'Failed to save subject');
    }
  };

  const openAddModal = () => {
    setEditingSubject(null);
    setFormData({ subject_name: '', subject_code: '', description: '' });
    setShowModal(true);
    setError('');
  };

  const openEditModal = (subject) => {
    setEditingSubject(subject);
    setFormData({
      subject_name: subject.subject_name || '',
      subject_code: subject.subject_code || '',
      description: subject.description || ''
    });
    setShowModal(true);
    setError('');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Manage Subjects</h1>
          <p className="text-gray-500">Configure subjects in the system</p>
        </div>
        <button onClick={openAddModal} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
          <Plus className="w-5 h-5" /> Add Subject
        </button>
      </div>

      {subjects.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <p className="text-gray-400">No subjects configured yet. Add your first subject to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {subjects.map(subject => (
                <div 
                  key={subject.subject_id} 
                  onClick={() => navigate(`/admin/subjects/${subject.subject_id}`)}
                  className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:bg-indigo-50 transition cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{subject.subject_name}</h4>
                      <p className="text-sm text-indigo-600">{subject.subject_code}</p>
                      {subject.description && <p className="text-sm text-gray-500 mt-1">{subject.description}</p>}
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(subject);
                      }}
                      className="p-2 hover:bg-blue-50 text-blue-500 rounded-lg transition"
                      title="Edit Subject"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Subject Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">{editingSubject ? 'Edit Subject' : 'Add New Subject'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

            <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
              <strong>Note:</strong> After creating a subject, assign it to grades from the <button type="button" onClick={() => { setShowModal(false); navigate('/admin/grades'); }} className="underline font-semibold hover:text-blue-800">Grades</button> page.
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Name *</label>
                <input type="text" required value={formData.subject_name} onChange={(e) => setFormData({...formData, subject_name: e.target.value})}
                  placeholder="e.g., English Poetry" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Code *</label>
                <input type="text" required value={formData.subject_code} onChange={(e) => setFormData({...formData, subject_code: e.target.value})}
                  placeholder="e.g., ENG101" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{editingSubject ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
